import { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import BulkAPI from "../api/BulkAPI";
import { computeBulkSummary } from "../components/bulk/bulkObjects";
import {
  runBulkIngestMutation,
  setBulkStorefrontProjectMutation,
} from "../components/bulk/queries/mutations";

import type {
  BulkIngestStatus,
  GetBulkStatusResponse,
} from "../api/BulkAPI.types";
import type { BulkSummary } from "../components/bulk/bulkObjects";

export const BULK_POLL_INTERVAL_MS = 2000;

export function getBulkRefetchInterval(
  status: BulkIngestStatus | undefined,
  hasRunSucceeded: boolean,
): number | false {
  if (status === "doing") {
    return BULK_POLL_INTERVAL_MS;
  }

  if (status === "created" && hasRunSucceeded) {
    return BULK_POLL_INTERVAL_MS;
  }

  return false;
}

export type StartIngestBlockedReason =
  | "starting"
  | "savingStorefrontProject"
  | "storefrontProjectRequired";

interface StartIngestBlockedReasonParams {
  isStartingIngest: boolean;
  isSavingStorefrontProject: boolean;
  requiresStorefrontProject: boolean;
  storefrontProjectId: string | null;
}

export function getStartIngestBlockedReason({
  isStartingIngest,
  isSavingStorefrontProject,
  requiresStorefrontProject,
  storefrontProjectId,
}: StartIngestBlockedReasonParams): StartIngestBlockedReason | null {
  if (isStartingIngest) {
    return "starting";
  }

  if (isSavingStorefrontProject) {
    return "savingStorefrontProject";
  }

  if (
    requiresStorefrontProject &&
    (storefrontProjectId === null || storefrontProjectId.length === 0)
  ) {
    return "storefrontProjectRequired";
  }

  return null;
}

interface UseBulkIngestResult {
  ingest: GetBulkStatusResponse | undefined;
  status: BulkIngestStatus | undefined;
  summary: BulkSummary;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  startIngest: () => void;
  isBusy: boolean;
  requiresStorefrontProject: boolean;
  storefrontProjectId: string | null;
  pendingStorefrontProjectId: string | null;
  setStorefrontProject: (sfpUuid: string) => void;
  isSettingStorefrontProject: boolean;
  startIngestBlockedReason: StartIngestBlockedReason | null;
}

export default function useBulkIngest(bulkId: string): UseBulkIngestResult {
  const queryClient = useQueryClient();

  const runMutation = useMutation(
    runBulkIngestMutation({ bulkId, queryClient }),
  );

  const storefrontProjectMutation = useMutation(
    setBulkStorefrontProjectMutation({ bulkId, queryClient }),
  );

  const bulkStatusQuery = BulkAPI.generateQuery({
    fn: BulkAPI.getBulkStatus,
    params: [{ bulkId }],
    options: { enabled: bulkId.length > 0 },
  });

  const { data, isLoading, isError, refetch } = useQuery({
    ...bulkStatusQuery,
    refetchInterval: (response) =>
      getBulkRefetchInterval(
        response?.more.bulk_ingest_status,
        runMutation.isSuccess,
      ),
  });

  const status = data?.more.bulk_ingest_status;

  const summary = useMemo(
    () => computeBulkSummary(data?.data ?? []),
    [data?.data],
  );

  const requiresStorefrontProject =
    data?.more.requires_storefront_project ?? false;
  const storefrontProjectId = data?.more.sfp_uuid ?? null;
  const isRunAccepted = runMutation.isSuccess && status === "created";
  const isStartingIngest = runMutation.isLoading || isRunAccepted;
  const isBusy = isStartingIngest || storefrontProjectMutation.isLoading;
  const startIngestBlockedReason = getStartIngestBlockedReason({
    isStartingIngest,
    isSavingStorefrontProject: storefrontProjectMutation.isLoading,
    requiresStorefrontProject,
    storefrontProjectId,
  });

  return {
    ingest: data,
    status,
    summary,
    isLoading,
    isError,
    refetch: (): void => {
      refetch().catch(() => undefined);
    },
    startIngest: (): void => {
      runMutation.mutate();
    },
    isBusy,
    requiresStorefrontProject,
    storefrontProjectId,
    pendingStorefrontProjectId: storefrontProjectMutation.isLoading
      ? (storefrontProjectMutation.variables ?? null)
      : null,
    setStorefrontProject: (sfpUuid: string): void => {
      if (isBusy) {
        return;
      }

      storefrontProjectMutation.mutate(sfpUuid);
    },
    isSettingStorefrontProject: storefrontProjectMutation.isLoading,
    startIngestBlockedReason,
  };
}
